/**
 * "Not for me because..." feedback and what the match has learned from it (TEC-744, spec 6.9).
 *
 * The user's answers live in the `matchFeedback` user-state item as plain entries. Everything the
 * match "has learned" is derived from those entries on read, deterministically, so the visible
 * list, the re-ranking and the reset all work off one source of truth and nothing hides in a model.
 * The adjustment only moves jobs down inside the sort (it is a sort, never a filter).
 */
import { pickAdmin1Region } from '../plus-data/regions';
import { ROLE_LEVEL_HIERARCHY } from '../data/calculate-job-match-score';

export const MATCH_FEEDBACK_REASONS = [
  'tooJunior',
  'tooSenior',
  'tooFar',
  'wrongCompany',
  'needsFinnish',
  'payTooLow',
  'consulting',
] as const;
export type MatchFeedbackReason = (typeof MATCH_FEEDBACK_REASONS)[number];

/** What we remember about a rejected job, just enough to find similar ones. No ad text. */
export interface MatchFeedbackSignature {
  /** 1 (intern) to 8 (executive), from ROLE_LEVEL_HIERARCHY. */
  levelRank?: number;
  /** Gazetteer admin1 region of the job, when it has one. */
  region?: string;
  remote?: boolean;
  industries?: string[];
  companyId?: string;
  /** Stated pay as monthly EUR (the lower end). */
  payMonthly?: number;
}

export interface MatchFeedbackEntry {
  id: string;
  reason: MatchFeedbackReason;
  jobId: string;
  at: string;
  signature: MatchFeedbackSignature;
}

export interface MatchFeedbackState {
  entries: MatchFeedbackEntry[];
  updatedAt: string | null;
}

export const EMPTY_MATCH_FEEDBACK: MatchFeedbackState = { entries: [], updatedAt: null };

/** Keeps the stored item small and the effect bounded. */
export const MATCH_FEEDBACK_MAX_ENTRIES = 120;
/** Points taken off a job's match for the first matching answer, and for each further one, up to the cap. */
export const MATCH_PENALTY_FIRST = 10;
export const MATCH_PENALTY_STEP = 6;
export const MATCH_PENALTY_CAP = 30;

type JobLike = Record<string, unknown>;

function asStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (typeof value === 'string' && value) return [value];
  return [];
}

/** Highest level rank among the job's level values (a job can carry several), or undefined. */
export function levelRankOf(job: JobLike): number | undefined {
  const ranks = asStringList(job.normalisedRoleLevel)
    .map((l) => ROLE_LEVEL_HIERARCHY[l.toLowerCase().trim()] ?? 0)
    .filter((n) => n > 0);
  return ranks.length ? Math.max(...ranks) : undefined;
}

function monthlyPayOf(job: JobLike): number | undefined {
  const min = typeof job.salaryRangeMin === 'number' ? job.salaryRangeMin : undefined;
  if (!min || min <= 0) return undefined;
  if (job.salaryIsEstimate === true) return undefined;
  const period = job.salaryPeriod;
  if (period === 'annual') return min / 12;
  if (period === 'daily') return min * 22;
  if (period === 'hourly') return min * 160;
  return min;
}

function isRemote(job: JobLike): boolean {
  return String(job.remoteness ?? '').toLowerCase() === 'remote';
}

function requiresFinnish(job: JobLike): boolean {
  return asStringList(job.requiredLanguages).some((l) => /^(finnish|suomi|finska)$/i.test(l.trim()));
}

function isConsulting(job: JobLike): boolean {
  return job.isConsultingCompany === true || job.isRecruitmentCompany === true;
}

export function signatureOf(job: JobLike): MatchFeedbackSignature {
  const sig: MatchFeedbackSignature = {};
  const rank = levelRankOf(job);
  if (rank !== undefined) sig.levelRank = rank;
  const region = pickAdmin1Region(asStringList(job.regionTags));
  if (region) sig.region = region;
  if (isRemote(job)) sig.remote = true;
  const industries = asStringList(job.companyIndustry).slice(0, 4);
  if (industries.length) sig.industries = industries;
  const companyId = typeof job.companyId === 'string' ? job.companyId : undefined;
  if (companyId) sig.companyId = companyId;
  const pay = monthlyPayOf(job);
  if (pay) sig.payMonthly = Math.round(pay);
  return sig;
}

function isReason(value: unknown): value is MatchFeedbackReason {
  return typeof value === 'string' && (MATCH_FEEDBACK_REASONS as readonly string[]).includes(value);
}

function coerceSignature(raw: unknown): MatchFeedbackSignature {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const sig: MatchFeedbackSignature = {};
  if (typeof r.levelRank === 'number' && r.levelRank >= 1 && r.levelRank <= 8) sig.levelRank = r.levelRank;
  if (typeof r.region === 'string' && r.region.length <= 40) sig.region = r.region;
  if (r.remote === true) sig.remote = true;
  const industries = asStringList(r.industries).slice(0, 4).map((s) => s.slice(0, 80));
  if (industries.length) sig.industries = industries;
  if (typeof r.companyId === 'string' && r.companyId.length <= 200) sig.companyId = r.companyId;
  if (typeof r.payMonthly === 'number' && r.payMonthly > 0 && r.payMonthly < 1_000_000) sig.payMonthly = Math.round(r.payMonthly);
  return sig;
}

/** Reads a stored item defensively: anything malformed is dropped, never thrown on. */
export function coerceMatchFeedback(raw: unknown): MatchFeedbackState {
  if (!raw || typeof raw !== 'object') return EMPTY_MATCH_FEEDBACK;
  const r = raw as Record<string, unknown>;
  const list = Array.isArray(r.entries) ? r.entries : [];
  const entries: MatchFeedbackEntry[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const e = item as Record<string, unknown>;
    if (!isReason(e.reason) || typeof e.id !== 'string' || typeof e.jobId !== 'string') continue;
    entries.push({
      id: e.id.slice(0, 64),
      reason: e.reason,
      jobId: e.jobId.slice(0, 200),
      at: typeof e.at === 'string' ? e.at : new Date(0).toISOString(),
      signature: coerceSignature(e.signature),
    });
  }
  return {
    entries: entries.slice(0, MATCH_FEEDBACK_MAX_ENTRIES),
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : null,
  };
}

/** Adds one answer for a job. The same reason for the same job twice is one answer, not two. */
export function addMatchFeedback(
  state: MatchFeedbackState,
  input: { reason: MatchFeedbackReason; jobId: string; job: JobLike },
  now: string,
): { state: MatchFeedbackState; entry: MatchFeedbackEntry } {
  const existing = state.entries.find((e) => e.jobId === input.jobId && e.reason === input.reason);
  if (existing) return { state, entry: existing };
  const entry: MatchFeedbackEntry = {
    id: `${now.slice(0, 19).replace(/\D/g, '')}${Math.random().toString(36).slice(2, 6)}`,
    reason: input.reason,
    jobId: input.jobId,
    at: now,
    signature: signatureOf(input.job),
  };
  return {
    state: { entries: [entry, ...state.entries].slice(0, MATCH_FEEDBACK_MAX_ENTRIES), updatedAt: now },
    entry,
  };
}

export function removeMatchFeedbackEntry(state: MatchFeedbackState, id: string, now: string): MatchFeedbackState {
  if (!state.entries.some((e) => e.id === id)) return state;
  return { entries: state.entries.filter((e) => e.id !== id), updatedAt: now };
}

export function removeMatchFeedbackReason(state: MatchFeedbackState, reason: MatchFeedbackReason, now: string): MatchFeedbackState {
  if (!state.entries.some((e) => e.reason === reason)) return state;
  return { entries: state.entries.filter((e) => e.reason !== reason), updatedAt: now };
}

export function resetMatchFeedback(now: string): MatchFeedbackState {
  return { entries: [], updatedAt: now };
}

/** One row of "What your match has learned". */
export interface LearnedItem {
  reason: MatchFeedbackReason;
  count: number;
}

export function deriveLearnedItems(state: MatchFeedbackState): LearnedItem[] {
  const counts = new Map<MatchFeedbackReason, number>();
  for (const e of state.entries) counts.set(e.reason, (counts.get(e.reason) ?? 0) + 1);
  return MATCH_FEEDBACK_REASONS.filter((r) => counts.has(r)).map((reason) => ({ reason, count: counts.get(reason) ?? 0 }));
}

function penaltyFor(count: number): number {
  return Math.min(MATCH_PENALTY_CAP, MATCH_PENALTY_FIRST + MATCH_PENALTY_STEP * (count - 1));
}

function looksLike(job: JobLike, reason: MatchFeedbackReason, group: MatchFeedbackEntry[]): number {
  // Returns a 0..1 strength of resemblance to what the user said they did not want.
  switch (reason) {
    case 'tooJunior': {
      const rank = levelRankOf(job);
      const ceiling = Math.max(0, ...group.map((e) => e.signature.levelRank ?? 0));
      return rank !== undefined && ceiling > 0 && rank <= ceiling ? 1 : 0;
    }
    case 'tooSenior': {
      const rank = levelRankOf(job);
      const floors = group.map((e) => e.signature.levelRank).filter((n): n is number => typeof n === 'number');
      return rank !== undefined && floors.length > 0 && rank >= Math.min(...floors) ? 1 : 0;
    }
    case 'tooFar': {
      if (isRemote(job)) return 0;
      const regions = new Set(group.map((e) => e.signature.region).filter((r): r is string => Boolean(r)));
      const region = pickAdmin1Region(asStringList(job.regionTags));
      return region && regions.has(region) ? 1 : 0;
    }
    case 'wrongCompany': {
      const companyId = typeof job.companyId === 'string' ? job.companyId : '';
      if (companyId && group.some((e) => e.signature.companyId === companyId)) return 1;
      const industries = new Set(group.flatMap((e) => e.signature.industries ?? []));
      return asStringList(job.companyIndustry).some((i) => industries.has(i)) ? 0.6 : 0;
    }
    case 'needsFinnish':
      return requiresFinnish(job) ? 1 : 0;
    case 'payTooLow': {
      const pay = monthlyPayOf(job);
      const ceiling = Math.max(0, ...group.map((e) => e.signature.payMonthly ?? 0));
      return pay !== undefined && ceiling > 0 && pay <= ceiling ? 1 : 0;
    }
    case 'consulting':
      return isConsulting(job) ? 1 : 0;
  }
}

/**
 * Points (zero or negative) to add to a job's match for the answers given so far. Each reason
 * counts once per matching job however many answers back it up; more answers make it stronger.
 */
export function matchAdjustmentFor(job: JobLike, state: MatchFeedbackState): number {
  if (state.entries.length === 0) return 0;
  let total = 0;
  for (const item of deriveLearnedItems(state)) {
    const group = state.entries.filter((e) => e.reason === item.reason);
    total -= penaltyFor(item.count) * looksLike(job, item.reason, group);
  }
  return Math.max(-60, Math.round(total));
}
